// Pure rules of a workflow version (G8, G9, C1; LH-F3 rows F3-1 … F3-3). No React, no store: the canvas, the
// endpoint screens and the stores call these, the tests check them. After the_Kit's packages/workflow/src/rules
// (paths.ts, graph.ts, checks.ts), rewritten for the lender node pack: Start (PI info), API (one batch file, one
// port per outcome its test events declare + error), Decision (a condition, no code), Wait, Answer (seven kinds).
import { endpointExists, endpointLabel, outcomesOf } from '@/lib/endpoints'
import type { AnswerKind, Batch, Condition, FlowEdge, FlowNode, FlowStatus, Lender, Product, ServiceEndpoint, WorkflowVersion } from '@/types'
import { ANSWER_KINDS } from '@/types'
import { batchesOfProduct, codeKeyOf, compareVersions } from '@/lib/batch'

export const FLOW_STATUS_LABEL: Record<FlowStatus, string> = { draft: 'draft', paths_passed: 'paths passed', live: 'live', previous: 'previous', retired: 'retired' }
export const isFlowEditable = (f: WorkflowVersion) => f.status === 'draft' || f.status === 'paths_passed'

/** The output ports of a node. API: the outcomes the endpoint declares and its test events expect, then `error` (always there). */
export function outputsOf(node: FlowNode, batch: Batch | undefined): string[] {
  switch (node.kind) {
    case 'start':
      return ['next']
    case 'decision':
      return ['yes', 'no']
    case 'wait':
      return ['done']
    case 'answer':
      return []
    case 'api':
      return batch && node.endpoint ? outcomesOf(batch.manifest, batch.tests, node.endpoint) : ['error']
  }
}

// ---- conditions (no code: field · operator · value) ---------------------------------------------------------------

export const CONDITION_FIELDS: { value: string; label: string }[] = [
  { value: 'prev.reply_code', label: 'last reply code (prev.reply_code)' },
  { value: 'prev.outcome', label: 'last outcome (prev.outcome)' },
  { value: 'prev.offers.length', label: 'number of offers (prev.offers.length)' },
  { value: 'prev.products', label: 'products the lender lists (prev.products)' },
  { value: 'prev.lender_ref', label: 'lender reference (prev.lender_ref)' },
  { value: 'applicant.incomeMonth', label: 'monthly income (applicant.incomeMonth)' },
  { value: 'applicant.employment', label: 'employment (applicant.employment)' },
  { value: 'applicant.loanAmount', label: 'amount asked (applicant.loanAmount)' },
  { value: 'applicant.pincode', label: 'pincode (applicant.pincode)' },
]
export const OPS: { value: Condition['op']; label: string }[] = [
  { value: 'eq', label: '=' }, { value: 'neq', label: '≠' }, { value: 'gt', label: '>' }, { value: 'gte', label: '≥' }, { value: 'lt', label: '<' }, { value: 'lte', label: '≤' },
  { value: 'contains', label: 'contains' }, { value: 'exists', label: 'is set' }, { value: 'missing', label: 'is not set' },
]

export function getPath(ctx: unknown, path: string): unknown {
  let cur: unknown = ctx
  for (const part of path.split('.')) {
    if (cur === null || cur === undefined) return undefined
    if (part === 'length' && (Array.isArray(cur) || typeof cur === 'string')) return cur.length
    cur = typeof cur === 'object' ? (cur as Record<string, unknown>)[part] : undefined
  }
  return cur
}

/** Evaluates a condition against the run context; numbers compare as numbers, everything else as text. Never runs code. */
export function evalCondition(c: Condition, ctx: unknown): boolean {
  const v = getPath(ctx, c.field)
  if (c.op === 'exists') return v !== undefined && v !== null && v !== ''
  if (c.op === 'missing') return v === undefined || v === null || v === ''
  if (c.op === 'contains') return Array.isArray(v) ? v.map(String).includes(c.value) : String(v ?? '').includes(c.value)
  const num = typeof v === 'number' || (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v)))
  const both = num && c.value.trim() !== '' && !Number.isNaN(Number(c.value))
  const a = both ? Number(v) : String(v ?? '')
  const b = both ? Number(c.value) : c.value
  switch (c.op) {
    case 'eq': return a === b
    case 'neq': return a !== b
    case 'gt': return a > b
    case 'gte': return a >= b
    case 'lt': return a < b
    case 'lte': return a <= b
  }
}

export const conditionText = (c: Condition) => `${c.field} ${OPS.find((o) => o.value === c.op)?.label ?? c.op}${c.op === 'exists' || c.op === 'missing' ? '' : ` ${c.value}`}`

/** The lines leaving one port, conditional ones first (tried in order), then the default. */
export function edgesFrom(flow: Pick<WorkflowVersion, 'edges'>, nodeId: string, port: string): FlowEdge[] {
  const out = flow.edges.filter((e) => e.from === nodeId && e.when === port)
  return [...out.filter((e) => e.condition), ...out.filter((e) => !e.condition)]
}

// ---- checks -------------------------------------------------------------------------------------------------------

export interface FlowIssue {
  nodeId?: string
  edgeId?: string
  text: string
}

/** What must be right before Run all paths can pass. `tokenRoutes` = the token-source endpoints a workflow may never call (F2-6). */
export function checkFlow(flow: Pick<WorkflowVersion, 'nodes' | 'edges'>, batch: Batch | undefined, tokenRoutes: string[] = []): FlowIssue[] {
  const issues: FlowIssue[] = []
  const starts = flow.nodes.filter((n) => n.kind === 'start')
  if (starts.length !== 1) issues.push({ text: starts.length ? 'More than one Start node' : 'No Start node (the PI info input)' })
  if (!batch) issues.push({ text: 'The API batch it uses does not exist' })
  for (const n of flow.nodes) {
    if (n.kind === 'api') {
      if (!n.endpoint) issues.push({ nodeId: n.id, text: `${n.name}: no endpoint chosen` })
      else if (batch && !endpointExists(batch.manifest, n.endpoint)) issues.push({ nodeId: n.id, text: `${n.name}: endpoint ${n.endpoint} is not in batch ${batch.version}` })
      if (n.endpoint && (tokenRoutes.includes(n.endpoint) || batch?.manifest.endpoints.find((e) => e.path === n.endpoint)?.tokenSource)) issues.push({ nodeId: n.id, text: `${n.name}: ${n.endpoint} is the token source; the Gateway calls it, a workflow never does` })
    }
    if (n.kind === 'answer' && !n.message?.trim()) issues.push({ nodeId: n.id, text: `${n.name}: no message for our user` })
    if (n.kind === 'decision' && !n.condition?.field) issues.push({ nodeId: n.id, text: `${n.name}: no condition` })
  }
  for (const e of flow.edges) {
    const from = flow.nodes.find((n) => n.id === e.from)
    const to = flow.nodes.find((n) => n.id === e.to)
    if (!from || !to) { issues.push({ edgeId: e.id, text: 'A line points to a node that is gone' }); continue }
    if (to.kind === 'start') issues.push({ edgeId: e.id, text: `A line goes into Start from ${from.name}` })
    if (!outputsOf(from, batch).includes(e.when)) issues.push({ edgeId: e.id, text: `${from.name}: port “${e.when}” does not exist any more (the endpoint no longer declares it)` })
  }
  const reach = reachable(flow)
  for (const n of flow.nodes) if (!reach.has(n.id) && n.kind !== 'start') issues.push({ nodeId: n.id, text: `${n.name}: not reachable from Start` })
  return issues
}

function reachable(flow: Pick<WorkflowVersion, 'nodes' | 'edges'>): Set<string> {
  const start = flow.nodes.find((n) => n.kind === 'start')
  const seen = new Set<string>()
  const stack = start ? [start.id] : []
  while (stack.length) {
    const id = stack.pop()!
    if (seen.has(id)) continue
    seen.add(id)
    for (const e of flow.edges) if (e.from === id) stack.push(e.to)
  }
  return seen
}

// ---- Run all paths ------------------------------------------------------------------------------------------------

export interface FlowPath {
  /** node names in order */
  nodes: string[]
  /** the port taken at each hop */
  hops: string[]
  /** the Answer reached, or null */
  answer: AnswerKind | null
  /** answer = ends at an Answer · poll = goes back through a Wait (allowed: polling) · dead-end = a port with no line · loop = back without a Wait · cut = too long */
  endsAt: 'answer' | 'poll' | 'dead-end' | 'loop' | 'cut'
}
export const pathOk = (p: FlowPath) => p.endsAt === 'answer' || p.endsAt === 'poll'

/** Every route a run can take: each port of each node, each line from it (conditional and default). Bounded. */
export function enumeratePaths(flow: Pick<WorkflowVersion, 'nodes' | 'edges'>, batch: Batch | undefined, maxPaths = 300, maxDepth = 30): FlowPath[] {
  const paths: FlowPath[] = []
  const start = flow.nodes.find((n) => n.kind === 'start')
  if (!start) return paths
  const walk = (id: string, names: string[], hops: string[], visited: string[]) => {
    if (paths.length >= maxPaths) return
    const node = flow.nodes.find((n) => n.id === id)
    if (!node) return
    const nextNames = [...names, node.name]
    if (node.kind === 'answer') return void paths.push({ nodes: nextNames, hops, answer: node.answer ?? 'error', endsAt: 'answer' })
    if (visited.includes(id)) {
      const loopPart = visited.slice(visited.indexOf(id)).map((v) => flow.nodes.find((n) => n.id === v)?.kind)
      return void paths.push({ nodes: nextNames, hops, answer: null, endsAt: loopPart.includes('wait') ? 'poll' : 'loop' })
    }
    if (hops.length >= maxDepth) return void paths.push({ nodes: nextNames, hops, answer: null, endsAt: 'cut' })
    for (const port of outputsOf(node, batch)) {
      const lines = edgesFrom(flow, id, port)
      if (!lines.length) {
        paths.push({ nodes: nextNames, hops: [...hops, port], answer: null, endsAt: 'dead-end' })
        continue
      }
      for (const e of lines) walk(e.to, nextNames, [...hops, e.condition ? `${port} if ${conditionText(e.condition)}` : port], [...visited, id])
      // only conditional lines on this port: when none holds, the run has nowhere to go
      if (lines.every((e) => e.condition)) paths.push({ nodes: nextNames, hops: [...hops, `${port} (no condition holds)`], answer: null, endsAt: 'dead-end' })
    }
  }
  walk(start.id, [], [], [])
  return paths
}

// ---- batches the workflow uses, C1 --------------------------------------------------------------------------------

/** The batch a workflow version runs: the lender's live batch, or the pinned version. */
/** BP-1: a workflow runs its product's batch, else the lender's whole-lender batch (api2's flow.rules.ts batchOf). */
export function batchOf(flow: Pick<WorkflowVersion, 'lenderSlug' | 'batch' | 'productSlug'>, batches: Batch[]): Batch | undefined {
  const mine = batchesOfProduct(batches, flow.lenderSlug, flow.productSlug)
  const pin = flow.batch
  return pin.mode === 'live' ? mine.find((b) => b.status === 'live') : mine.find((b) => b.version === pin.version)
}

export const batchLabel = (flow: Pick<WorkflowVersion, 'lenderSlug' | 'batch' | 'productSlug'>, batches: Batch[]) => {
  const b = batchOf(flow, batches)
  return flow.batch.mode === 'live' ? `${b?.version ?? 'none'} (live)` : `${flow.batch.version} (${b ? b.status.replace('_', ' ') : 'missing'}, pinned)`
}

/**
 * C1: before a batch goes live (or is rolled back to), every live workflow that follows the live batch must still
 * find each endpoint it calls (in the manifest) and each port its lines leave from. Returns the problems; empty = the workflows may move.
 */
export function c1Check(flow: Pick<WorkflowVersion, 'name' | 'version' | 'nodes' | 'edges'>, target: Batch): string[] {
  const out: string[] = []
  for (const n of flow.nodes.filter((x) => x.kind === 'api')) {
    if (!n.endpoint || !endpointExists(target.manifest, n.endpoint)) {
      out.push(`${flow.name} ${flow.version}: ${n.name} calls ${n.endpoint ?? '(none)'}, not in ${target.version}`)
      continue
    }
    const ports = outputsOf(n, target)
    for (const e of flow.edges.filter((x) => x.from === n.id)) if (!ports.includes(e.when)) out.push(`${flow.name} ${flow.version}: ${n.name} → “${e.when}” is not an outcome of ${n.endpoint} in ${target.version}`)
  }
  return out
}

/** The live workflows that move with the lender's live batch (C1 applies to them). */
export const followersOfLive = (flows: WorkflowVersion[], lenderSlug: string) => flows.filter((f) => f.lenderSlug === lenderSlug && f.status === 'live' && f.batch.mode === 'live')
/** BP-1: the live workflows that move with this batch: those whose product runs on its batch code. */
export const followersOfBatch = (flows: WorkflowVersion[], batches: Batch[], target: Batch) =>
  followersOfLive(flows, target.lenderSlug).filter((f) => batchesOfProduct(batches, f.lenderSlug, f.productSlug).some((b) => codeKeyOf(b) === codeKeyOf(target)))

// ---- endpoints ----------------------------------------------------------------------------------------------------

export type EndpointState = 'live' | 'test' | 'suspended' | 'lender_off' | 'product_inactive' | 'retired'
export const ENDPOINT_STATE_LABEL: Record<EndpointState, string> = { live: 'live', test: 'test (UAT only)', suspended: 'suspended', lender_off: 'suspended (lender off)', product_inactive: 'product inactive', retired: 'retired' }

/** What the web app sees: retired > lender off > suspended by an admin > product inactive > live / test. */
export function effectiveState(ep: ServiceEndpoint, lender: Lender | undefined, product: Product | undefined): EndpointState {
  if (ep.state === 'retired') return 'retired'
  if (!lender || lender.status !== 'active') return 'lender_off'
  if (ep.state === 'suspended') return 'suspended'
  if (product && !product.active) return 'product_inactive'
  return ep.env === 'uat' ? 'test' : 'live'
}

/** Which versions an endpoint may run. Production: passed paths and a built batch. UAT only: anything not retired. */
export function switchable(ep: ServiceEndpoint, flow: WorkflowVersion, batches: Batch[]): { ok: boolean; why: string | null } {
  if (flow.name !== ep.workflowName) return { ok: false, why: 'another workflow' }
  if (flow.status === 'retired') return { ok: false, why: 'retired' }
  if (ep.env === 'uat') return { ok: true, why: null }
  if (flow.status === 'draft') return { ok: false, why: 'draft: Run all paths first' }
  const b = batchOf(flow, batches)
  if (!b) return { ok: false, why: 'its batch is missing' }
  if (b.status === 'draft' || b.status === 'tests_passed') return { ok: false, why: `pins draft batch ${b.version}` }
  return { ok: true, why: null }
}

export interface SwitchResult {
  flows: WorkflowVersion[]
  endpoint: ServiceEndpoint
  from: string
}

/**
 * Switch the version behind an endpoint (one click, G9). On a production endpoint the new version becomes live, the
 * one it replaces becomes previous, an older previous of the same workflow becomes retired.
 */
export function applySwitch(flows: WorkflowVersion[], ep: ServiceEndpoint, toVersion: string, batches: Batch[], who: string, at: string): SwitchResult {
  const target = flows.find((f) => f.name === ep.workflowName && f.version === toVersion)
  if (!target) throw new Error(`${ep.workflowName} ${toVersion} does not exist`)
  const can = switchable(ep, target, batches)
  if (!can.ok) throw new Error(`${ep.workflowName} ${toVersion} cannot run behind ${ep.id}: ${can.why}`)
  const from = ep.workflowVersion
  if (ep.env === 'uat_prod' && from === toVersion) {
    // the version a production endpoint was created for goes live in place (the endpoint itself does not change)
    if (target.status === 'live') throw new Error(`${ep.id} already runs ${toVersion}`)
    return { flows: flows.map((f) => (f.id === target.id ? { ...f, status: 'live' as const, madeLiveAt: at } : f)), endpoint: ep, from }
  }
  const endpoint: ServiceEndpoint = { ...ep, workflowVersion: toVersion, lastSwitch: { at, from, to: toVersion, by: who } }
  if (ep.env === 'uat' || from === toVersion) return { flows, endpoint, from }
  const next = flows.map((f) => {
    if (f.name !== ep.workflowName) return f
    if (f.version === toVersion) return { ...f, status: 'live' as const, madeLiveAt: at }
    if (f.version === from) return { ...f, status: 'previous' as const }
    if (f.status === 'previous') return { ...f, status: 'retired' as const }
    return f
  })
  return { flows: next, endpoint, from }
}

// ---- versions, save, paths run ------------------------------------------------------------------------------------

export function nextFlowVersion(flows: WorkflowVersion[], name: string): string {
  const top = flows.filter((f) => f.name === name).map((f) => f.version).sort(compareVersions).at(-1)
  if (!top) return '1.0.0'
  const [maj, min] = top.split('.').map(Number)
  return `${maj}.${(min ?? 0) + 1}.0`
}

/** Save: the drawing changed, so "paths passed" falls back to draft. */
export function applyFlowSave(f: WorkflowVersion, patch: Pick<WorkflowVersion, 'nodes' | 'edges' | 'settings' | 'batch'>, note: string, who: string, at: string): WorkflowVersion {
  if (!isFlowEditable(f)) throw new Error(`${f.name} ${f.version} is ${FLOW_STATUS_LABEL[f.status]} and read-only; New version from this to change it`)
  return { ...f, ...patch, status: 'draft', note: note.trim() || f.note, savedBy: who, savedAt: at }
}

export function applyPathsRun(f: WorkflowVersion, paths: FlowPath[], issues: FlowIssue[], who: string, at: string): WorkflowVersion {
  const ok = paths.filter(pathOk).length
  const pathsRun = { total: paths.length, ok, at, by: who }
  if (!isFlowEditable(f)) return { ...f, pathsRun }
  const green = paths.length > 0 && ok === paths.length && issues.length === 0
  return { ...f, pathsRun, status: green ? 'paths_passed' : 'draft' }
}

export function copyFlow(from: WorkflowVersion, version: string, who: string, at: string, override?: Partial<WorkflowVersion>): WorkflowVersion {
  return {
    ...JSON.parse(JSON.stringify(from)),
    id: `wf_${from.lenderSlug}_${version.replace(/\./g, '')}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`,
    version,
    status: 'draft',
    pathsRun: null,
    savedBy: who,
    savedAt: at,
    note: `New version from ${from.version}`,
    madeFrom: from.version,
    madeLiveAt: null,
    ...override,
  }
}

/** The workflow name for a product: moneyview.personal → moneyview.pl.flow (two letters per word of the product). */
export function flowNameFor(lenderSlug: string, product: Pick<Product, 'slug' | 'name'>): string {
  const short = product.name.toLowerCase().replace(/[^a-z ]/g, ' ').trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 3) || 'flow'
  return `${lenderSlug}.${short}.flow`
}

// ---- new workflows ------------------------------------------------------------------------------------------------

let seq = 0
export const newNodeId = () => `n_${Date.now().toString(36)}${(seq++).toString(36)}`
export const newEdgeId = () => `e_${Date.now().toString(36)}${(seq++).toString(36)}`

const ANSWER_MESSAGES: Record<AnswerKind, string> = {
  offer: '{lender} can offer you a loan. See the offer below.',
  approved: '{lender} has approved you in principle. Continue on their site.',
  redirect: 'Continue your application with {lender}.',
  pending: '{lender} is still deciding. We will tell you as soon as they answer.',
  rejected: '{lender} cannot offer you a loan today.',
  duplicate: 'You already have an application with {lender}. Continue it in their app.',
  error: '{lender} is not answering right now. Please try again in a few minutes.',
}
export const defaultMessage = (k: AnswerKind) => ANSWER_MESSAGES[k]
export { ANSWER_KINDS }

/** A new workflow: Start, the chosen endpoints in a row, every port of the last one to a matching Answer, the rest to error. */
export function starterFlow(routes: string[], batch: Batch | undefined): Pick<WorkflowVersion, 'nodes' | 'edges'> {
  const nodes: FlowNode[] = [{ id: 'start', kind: 'start', name: 'Start · PI info', pos: { x: 0, y: 200 } }]
  const edges: FlowEdge[] = []
  const answers = new Map<AnswerKind, string>()
  const answer = (k: AnswerKind) => {
    if (!answers.has(k)) {
      const id = `ans_${k}`
      answers.set(k, id)
      nodes.push({ id, kind: 'answer', name: `Answer · ${k}`, answer: k, message: defaultMessage(k), pos: { x: 280 * (routes.length + 1), y: 30 + 95 * answers.size } })
    }
    return answers.get(k)!
  }
  let prev = 'start'
  let prevPort = 'next'
  routes.forEach((route, i) => {
    const id = `api_${i}`
    nodes.push({ id, kind: 'api', name: endpointLabel(route).split('/').pop()!, endpoint: route, waitS: null, retries: 0, pos: { x: 280 * (i + 1), y: 200 } })
    edges.push({ id: newEdgeId(), from: prev, when: prevPort, to: id })
    const ports = outputsOf(nodes.at(-1)!, batch)
    const last = i === routes.length - 1
    const go = ports.find((p) => p === 'ok') ?? ports[0]!
    for (const p of ports) {
      if (!last && p === go) continue
      const k: AnswerKind = (ANSWER_KINDS as readonly string[]).includes(p) ? (p as AnswerKind) : p === 'ok' ? 'redirect' : 'error'
      edges.push({ id: newEdgeId(), from: id, when: p, to: answer(k) })
    }
    prev = id
    prevPort = go
  })
  if (!routes.length) edges.push({ id: newEdgeId(), from: 'start', when: 'next', to: answer('error') })
  return { nodes, edges }
}
